import React from "react";
import { useSetting, useSettingsActions } from "./SettingsStore.tsx";
import { SelectButton } from "primereact/selectbutton";
import { RadioButton } from "primereact/radiobutton";
import { SOLID_VOLUME_QUESTIONS, VOLUME_MAX_EDGE_OPTIONS, VolumeQuestionOptions } from "../Constants.ts";
import { withLabels } from "../Labels.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";

/**
 * The two volume dials (ADR 0095 §3, §4).
 *
 * Longest edge bounds the edges a labelled box is built from, not its volume,
 * which is a product of three of them. It does not apply to Count the cubes,
 * whose edges are always 2 to 5, so the dial is hidden for that question, as
 * the calendar panel hides its span for a sheet with no before-and-after
 * question. Mixed keeps it: two of its three questions use it, and so do
 * Two boxes and Surface Area (ADR 0097, 0100).
 *
 * Both are basic tier, as the format's own panel is (ADR 0023 §2). Reads its
 * own slice of the settings (ADR 0081), so a change elsewhere on the panel
 * does not redraw it.
 */
export const VolumeSettings = React.memo(function VolumeSettings() {
    const { getSettings, setSettings } = useSettingsActions();
    const { question, maxEdge, language } = useSetting((settings) => ({
        question: settings.VolumeQuestion,
        maxEdge: settings.VolumeMaxEdge,
        language: settings.Language,
    }));
    const t = useTranslation();
    // The four round-solid types bound a radius and a height, not an edge (ADR 0140 §3).
    // (Each key is written out whole: `StringKeys.test.js` finds them by scanning for `t('...')`.)
    const solids = question !== undefined && SOLID_VOLUME_QUESTIONS.includes(question);
    const dialLabel = solids ? t('settings.largestRadiusOrHeightLabel') : t('settings.longestEdgeLabel');
    const dialAria = solids ? t('settings.largestRadiusOrHeightAria') : t('settings.longestEdgeAria');

    function handleQuestionChange(value: VolumeQuestionOptions) {
        // SelectButton hands back null when the selected option is clicked
        // again; a sheet has to ask something.
        if (!value) {
            return;
        }
        setSettings({ ...getSettings(), VolumeQuestion: value });
    }

    return (
        <div className="volumeSettingsContainer">
            <div className="regroupingContainer">
                <SelectButton
                    value={question}
                    onChange={(e) => handleQuestionChange(e.value)}
                    options={withLabels(Object.values(VolumeQuestionOptions), language)}
                    className="select regrouping"
                    aria-label={t('settings.questionType')}
                />
            </div>
            {question !== VolumeQuestionOptions.CountCubes && (
                <>
                    <div className="selectContainer">
                        <span className="settingLabel">{dialLabel}</span>
                    </div>
                    <div className="radioButtons" role="radiogroup" aria-label={dialAria}>
                        {VOLUME_MAX_EDGE_OPTIONS.map((option) => (
                            <div key={option}>
                                <RadioButton
                                    inputId={`volumeMaxEdge-${option}`}
                                    value={option}
                                    checked={maxEdge === option}
                                    onChange={() => setSettings({ ...getSettings(), VolumeMaxEdge: option })}
                                />
                                <label htmlFor={`volumeMaxEdge-${option}`}>{option}</label>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
});

export default VolumeSettings;
